'use client';

import { MapPin, Plane, Handshake, Globe2 } from 'lucide-react';

const PILLARS = [
  {
    icon: MapPin,
    city: 'Dakar',
    country: 'Sénégal',
    role: 'Atelier des origines',
    text: "C'est ici, dans la lumière ocre de l'île de Gorée, que tout a commencé. Le cuir y parle wolof, les mains y connaissent les gestes des selliers touaregs. L'âme de la maison demeure à Dakar.",
  },
  {
    icon: Plane,
    city: 'Anvers',
    country: 'Belgique',
    role: 'Atelier de prestige',
    text: "Établi rue Nationale depuis 2014, l'atelier belge perpétue les gestes africains dans l'exigence européenne. C'est ici que naissent les pièces numérotées destinées au monde.",
  },
];

const EXCHANGES = [
  { title: 'Séjour de transmission', year: '2017', text: 'Cinq artisans de Dakar viennent partager les techniques de tannage végétal au bureau d\'Anvers pendant trois mois.' },
  { title: 'Bourse de formation', year: '2020', text: 'Trois apprentis belges partent étudier la patine à la cire d\'abeille dans l\'atelier sénégalais.' },
  { title: 'Collection Croisée', year: '2022', text: 'Lancement de la ligne « Téranga » : moitié cousue à Dakar, moitié à Anvers, signature des deux ateliers.' },
];

export default function Heritage() {
  return (
    <section
      id="heritage"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/images/heritage-panafricain.png"
          alt="Pont culturel entre Dakar et Anvers"
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.4) saturate(1.15)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,15,0.85) 0%, rgba(10,10,15,0.55) 50%, rgba(10,10,15,0.92) 100%)',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Héritage Panafricain</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-gold-gradient">Deux rives,</span>
            <br />
            <span className="text-[#F4E8C1]">une même main.</span>
          </h2>
          <p className="max-w-3xl mx-auto text-[#F4E8C1]/70 font-light leading-relaxed">
            MATAR STYLE CUIR est né d'une intuition : que le cuir ne connaît pas
            de frontières, et que les savoir-faire se partagent comme on
            partage un repas. De Dakar à Anvers, deux ateliers, une même âme.
          </p>
        </div>

        {/* Two cities — Dakar and Anvers */}
        <div className="grid lg:grid-cols-2 gap-6 mb-16">
          {PILLARS.map((p, idx) => (
            <div
              key={p.city}
              className="relative bg-[#12121a]/70 backdrop-blur-sm border border-[#D4AF37]/25 p-8 hover:border-[#D4AF37]/55 transition-colors duration-500"
            >
              {/* Top row: icon + role */}
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 rounded-full border border-[#D4AF37]/40 flex items-center justify-center">
                  <p.icon size={24} className="text-[#D4AF37]" />
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80 mb-1">
                    {p.role}
                  </div>
                  <div className="font-display text-xs uppercase tracking-[0.2em] text-[#F4E8C1]/60">
                    {idx === 0 ? 'Atelier Premier' : 'Atelier Royal'}
                  </div>
                </div>
              </div>

              {/* City name */}
              <div className="mb-4">
                <div className="font-display text-3xl sm:text-4xl font-bold text-gold-gradient">
                  {p.city}
                </div>
                <div className="text-sm text-[#F4E8C1]/70 italic font-serif mt-1">
                  {p.country}
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-[#F4E8C1]/70 leading-relaxed font-light mb-5">
                {p.text}
              </p>

              {/* Coordinates */}
              <div className="pt-4 border-t border-[#D4AF37]/15 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/50">
                  {idx === 0 ? '14°41\'N · 17°26\'O' : '51°13\'N · 04°24\'E'}
                </span>
                <span className="text-[#D4AF37] text-xs">✦</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bridge narrative */}
        <div className="bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-8 sm:p-10 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Handshake size={22} className="text-[#D4AF37]" />
            <h3 className="font-display text-xl font-semibold text-[#F4E8C1] tracking-wide uppercase">
              Le Pont des Savoir-Faire
            </h3>
          </div>

          <p className="text-[#F4E8C1]/75 leading-relaxed font-light mb-6 text-sm sm:text-base">
            Chaque année, les deux ateliers échangent des artisans, des
            techniques, des histoires. Cette circulation des gestes est la
            signature morale de la maison : refuser que le savoir soit jamais
            prisonnier d'un lieu.
          </p>

          {/* Exchange timeline */}
          <div className="grid sm:grid-cols-3 gap-4">
            {EXCHANGES.map((e) => (
              <div
                key={e.title}
                className="bg-[#0a0a0f]/60 border-l-2 border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors duration-300 p-4"
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2 font-semibold">
                  {e.year}
                </div>
                <h4 className="font-display text-sm font-semibold text-[#F4E8C1] mb-2 tracking-wide">
                  {e.title}
                </h4>
                <p className="text-xs text-[#F4E8C1]/60 leading-relaxed font-light">
                  {e.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Final manifesto */}
        <div className="text-center max-w-3xl mx-auto">
          <Globe2 size={32} className="text-[#D4AF37] mx-auto mb-4" />
          <p className="font-serif italic text-lg sm:text-xl text-[#F4E8C1]/85 leading-relaxed mb-5">
            « Nous ne fabriquons pas du cuir belge, ni du cuir sénégalais. Nous
            façonnons du cuir panafricain — c'est-à-dire un cuir qui a voyagé,
            qui a appris, qui a partagé. »
          </p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            — Manifeste de la Maison, 2014
          </p>
        </div>
      </div>
    </section>
  );
}
